<template>
  <div class="analyses-dashboard">
    <v-container class="analyses-dashboard-container" data-testid="analyses-dashboard">
      <div class="analyses-dashboard-shell">
        <AppAccountRail v-if="!isMobileNav" standalone class="analyses-dashboard-rail" />
        <div class="analyses-dashboard-main">
      <header class="dashboard-header">
        <p class="dashboard-eyebrow">{{ t('analysesDashboard.eyebrow') }}</p>
        <h1 class="dashboard-title">{{ t('analysesDashboard.title') }}</h1>
        <p class="dashboard-lede">{{ t('analysesDashboard.lede') }}</p>
      </header>

      <div class="dashboard-toolbar">
        <v-btn
          color="primary"
          variant="flat"
          prepend-icon="mdi-plus"
          data-testid="dashboard-new-analysis"
          @click="goToNewAnalysis"
        >
          {{ t('analysesDashboard.newAnalysisCta') }}
        </v-btn>
        <span v-if="!loading && analyses.length" class="dashboard-count">
          {{ t('analysesDashboard.count', { count: analyses.length }) }}
        </span>
      </div>

      <div v-if="!authStore.isAuthenticated" class="dashboard-state dashboard-state--gate" data-testid="dashboard-auth-gate">
        <v-icon icon="mdi-lock-outline" size="48" />
        <h2>{{ t('analysesDashboard.gate.title') }}</h2>
        <p>{{ t('analysesDashboard.gate.body') }}</p>
        <div class="dashboard-state-actions">
          <v-btn color="primary" variant="flat" to="/sign-in">{{ t('nav.auth.signIn') }}</v-btn>
          <v-btn variant="text" to="/sign-up">{{ t('nav.auth.signUp') }}</v-btn>
        </div>
      </div>

      <div v-else-if="loading" class="dashboard-state dashboard-state--loading" data-testid="dashboard-loading">
        <v-progress-circular indeterminate color="primary" size="32" width="3" />
        <span>{{ t('analysesDashboard.loading') }}</span>
      </div>

      <div v-else-if="error" class="dashboard-state dashboard-state--error" data-testid="dashboard-error">
        <v-icon icon="mdi-alert-circle-outline" size="36" />
        <h2>{{ t('analysesDashboard.errorTitle') }}</h2>
        <p>{{ t('analysesDashboard.errorBody') }}</p>
        <v-btn color="primary" variant="flat" @click="fetchAnalyses">
          {{ t('analysesDashboard.retry') }}
        </v-btn>
      </div>

      <div v-else-if="!analyses.length" class="dashboard-state dashboard-state--empty" data-testid="dashboard-empty">
        <v-icon icon="mdi-floor-plan" size="48" />
        <h2>{{ t('analysesDashboard.empty.title') }}</h2>
        <p>{{ t('analysesDashboard.empty.body') }}</p>
        <v-btn color="primary" variant="flat" prepend-icon="mdi-upload" @click="goToNewAnalysis">
          {{ t('analysesDashboard.empty.cta') }}
        </v-btn>
      </div>

      <div v-else class="dashboard-grid" data-testid="dashboard-grid">
        <article
          v-for="entry in analyses"
          :key="entry.id"
          class="dashboard-card"
          role="button"
          tabindex="0"
          :aria-label="t('analysesDashboard.openCardAria', { date: formatDate(entry.timestamp) })"
          :data-testid="`dashboard-card-${entry.id}`"
          @click="openAnalysis(entry.id)"
          @keydown.enter.prevent="openAnalysis(entry.id)"
          @keydown.space.prevent="openAnalysis(entry.id)"
        >
          <div class="dashboard-card-thumb" aria-hidden="true">
            <img v-if="entry.imageUrl" :src="entry.imageUrl" alt="" />
            <v-icon v-else icon="mdi-floor-plan" size="42" />
            <span v-if="entry.hasPremium" class="dashboard-card-pill">
              <v-icon icon="mdi-star-four-points" size="12" />
              {{ t('analysesDashboard.premiumBadge') }}
            </span>
          </div>
          <div class="dashboard-card-body">
            <p class="dashboard-card-date">
              <v-icon icon="mdi-clock-outline" size="14" />
              {{ formatDate(entry.timestamp) }}
            </p>
            <p v-if="entry.intentExpectations" class="dashboard-card-intent">
              <v-icon icon="mdi-format-quote-open" size="14" />
              {{ entry.intentExpectations }}
            </p>
            <p v-else-if="entry.impressionSummary" class="dashboard-card-summary">
              {{ entry.impressionSummary }}
            </p>
            <div class="dashboard-card-actions">
              <v-btn
                variant="flat"
                color="primary"
                size="small"
                density="comfortable"
                prepend-icon="mdi-view-dashboard-outline"
                :data-testid="`dashboard-open-360-${entry.id}`"
                @click.stop="openAnalysis(entry.id, '360')"
              >
                {{ t('analysis.productNav.report') }}
              </v-btn>
              <v-btn
                variant="tonal"
                color="primary"
                size="small"
                density="comfortable"
                prepend-icon="mdi-auto-fix"
                :data-testid="`dashboard-open-studio-${entry.id}`"
                @click.stop="openAnalysis(entry.id, 'studio')"
              >
                {{ t('analysis.productNav.studio') }}
              </v-btn>
            </div>
          </div>
        </article>
      </div>
        </div>
      </div>
    </v-container>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useDisplay } from 'vuetify';
import { getIdToken } from 'firebase/auth';
import axios from 'axios';
import { useAuthStore } from '@/stores/auth';
import AppAccountRail from '@/components/AppAccountRail.vue';

const { mdAndDown } = useDisplay();
const isMobileNav = computed(() => mdAndDown.value);

const router = useRouter();
const { t, locale } = useI18n();
const authStore = useAuthStore();

const analyses = ref([]);
const loading = ref(false);
const error = ref(false);

const dateFormatter = computed(
  () => new Intl.DateTimeFormat(locale.value, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }),
);

const formatDate = (value) => {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return dateFormatter.value.format(date);
};

const fetchAnalyses = async () => {
  if (!authStore.isAuthenticated) {
    analyses.value = [];
    return;
  }
  loading.value = true;
  error.value = false;
  try {
    const token = await getIdToken(authStore.user);
    const response = await axios.get('/api/user-analyses', {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (response.data?.success) {
      analyses.value = response.data.data || [];
    } else {
      error.value = true;
    }
  } catch (err) {
    console.error('Failed to fetch user analyses:', err);
    error.value = true;
  } finally {
    loading.value = false;
  }
};

const openAnalysis = (id, surface) => {
  if (!id) return;
  if (surface === 'studio') {
    router.push({ name: 'AnalysisStudio', params: { id } });
    return;
  }
  if (surface === '360') {
    router.push({ name: 'Analysis360', params: { id } });
    return;
  }
  // No surface argument → default to the report (360) view, which is
  // the most informative landing surface for a previously completed
  // analysis.
  router.push({ name: 'Analysis360', params: { id } });
};

const goToNewAnalysis = () => {
  router.push({ name: 'Analysis' });
};

onMounted(fetchAnalyses);

watch(() => authStore.isAuthenticated, fetchAnalyses);
</script>

<style scoped>
.analyses-dashboard {
  background: linear-gradient(180deg, #fbf8f3 0%, #ffffff 320px);
  min-height: 100vh;
}

.analyses-dashboard-container {
  max-width: 1200px;
  padding: 32px 24px 48px;
}

.analyses-dashboard-shell {
  display: grid;
  grid-template-columns: minmax(220px, 240px) minmax(0, 1fr);
  gap: 24px;
  align-items: start;
}

.analyses-dashboard-rail {
  position: sticky;
  top: 96px;
}

.analyses-dashboard-main {
  min-width: 0;
}

@media (max-width: 960px) {
  .analyses-dashboard-shell {
    display: block;
  }
}

.dashboard-header {
  margin-bottom: 18px;
}

.dashboard-eyebrow {
  margin: 0 0 6px;
  color: #76532a;
  font-size: 0.82rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.dashboard-title {
  margin: 0 0 10px;
  color: #172b33;
  font-size: 2rem;
  line-height: 1.18;
}

.dashboard-lede {
  margin: 0;
  color: #4a5b66;
  font-size: 1rem;
  line-height: 1.5;
  max-width: 640px;
}

.dashboard-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 14px;
  margin-bottom: 24px;
}

.dashboard-count {
  color: #55717c;
  font-size: 0.9rem;
}

.dashboard-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 12px;
  padding: 48px 24px;
  border: 1px dashed rgba(41, 70, 86, 0.18);
  border-radius: 16px;
  background: #ffffff;
  color: #2d4350;
}

.dashboard-state h2 {
  margin: 0;
  color: #172b33;
  font-size: 1.2rem;
}

.dashboard-state p {
  margin: 0;
  max-width: 480px;
  color: #4a5b66;
  font-size: 0.95rem;
  line-height: 1.5;
}

.dashboard-state--gate :deep(.v-icon),
.dashboard-state--empty :deep(.v-icon) {
  color: #b08454;
}

.dashboard-state--error :deep(.v-icon) {
  color: #b35041;
}

.dashboard-state--loading {
  flex-direction: row;
  justify-content: center;
  border-style: solid;
  border-color: rgba(41, 70, 86, 0.10);
  color: #4a5b66;
  font-size: 0.95rem;
}

.dashboard-state-actions {
  display: flex;
  gap: 8px;
  margin-top: 4px;
  flex-wrap: wrap;
  justify-content: center;
}

.dashboard-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 18px;
}

.dashboard-card {
  display: flex;
  flex-direction: column;
  min-width: 0;
  background: #ffffff;
  border: 1px solid rgba(41, 70, 86, 0.12);
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 12px 28px rgba(24, 39, 48, 0.06);
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.dashboard-card:hover,
.dashboard-card:focus-within {
  transform: translateY(-2px);
  box-shadow: 0 18px 38px rgba(24, 39, 48, 0.10);
}

/* Keyboard focus on the card itself (role="button") gets a clear ring so
 * keyboard users can see what they're about to open. */
.dashboard-card:focus-visible {
  outline: 2px solid #245346;
  outline-offset: 2px;
}

.dashboard-card-thumb {
  position: relative;
  aspect-ratio: 16 / 10;
  background: #eef2f4;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #78909a;
}

.dashboard-card-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.dashboard-card-pill {
  position: absolute;
  top: 10px;
  left: 10px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 999px;
  background: #f5ead9;
  color: #76532a;
  font-size: 0.72rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.dashboard-card-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px 14px 16px;
}

.dashboard-card-date {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  color: #55717c;
  font-size: 0.82rem;
  font-weight: 700;
}

.dashboard-card-intent {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin: 0;
  color: #4a3d2a;
  font-size: 0.92rem;
  font-style: italic;
  line-height: 1.4;
  overflow-wrap: anywhere;
}

.dashboard-card-intent :deep(.v-icon) {
  color: #b08454;
  margin-top: 2px;
  flex-shrink: 0;
}

.dashboard-card-summary {
  margin: 0;
  color: #2d4350;
  font-size: 0.92rem;
  line-height: 1.45;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  overflow: hidden;
}

.dashboard-card-actions {
  display: flex;
  gap: 8px;
  margin-top: 4px;
  flex-wrap: wrap;
}

@media (max-width: 600px) {
  .analyses-dashboard-container {
    padding: 22px 16px 80px;
  }
  .dashboard-title {
    font-size: 1.6rem;
  }
  .dashboard-grid {
    grid-template-columns: 1fr;
    gap: 14px;
  }
  .dashboard-state {
    padding: 32px 16px;
  }
  .dashboard-card-actions :deep(.v-btn) {
    flex: 1 1 auto;
  }
}
</style>
